import React from 'react';
import { isSelfServiceConfigured } from '../../lib/selfServiceSupabase';
import { apiClient, ApiError } from '../../lib/apiClient';
import type { CreativeSlotState } from '../../types/selfService';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';

const STORAGE_PREFIX = 'ctvbuyer.selfservice.draft.';
const SAVE_DEBOUNCE_MS = 1200;
const ANON_SLOT = 'anon';

import type { WaymarkStatus } from './waymark/types';

interface WaymarkDraftState {
  status: WaymarkStatus;
  sourceUrl: string | null;
  topicId: string | null;
  generationRequestId: string | null;
  videoId: string | null;
  renderedUrl: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

interface LoadedDraft {
  currentStep: number;
  formData: Record<string, unknown>;
  creatives: CreativeSlotState[] | null;
  lastSavedAt: string;
  waymark: WaymarkDraftState;
}

interface SaveInput {
  currentStep: number;
  formData: Record<string, unknown>;
  creatives?: CreativeSlotState[] | null;
}

// CTV-554: a draft is scoped to (station, advertiser-or-anon, user-or-anon)
// to match the server's `(user_id, station_slug, advertiser_id)` partial
// unique index on selfservice_drafts. In-progress drafts are personal — two
// teammates sharing a seat must not see or overwrite each other's drafts —
// and seat switching mid-wizard must not bleed form values across seats.
// Anonymous (pre-sign-in) drafts live under the `anon.anon` slot and are
// promoted into the (advertiser, user) slot on first sign-in. See
// docs/adr/0018-personal-self-service-drafts.md for the why.
function scope(
  advertiserId: string | null | undefined,
  userId: string | null | undefined,
): string {
  const adv = advertiserId && advertiserId.length > 0 ? advertiserId : ANON_SLOT;
  const usr = userId && userId.length > 0 ? userId : ANON_SLOT;
  return `${adv}.${usr}`;
}

function tokenKey(
  slug: string,
  advertiserId: string | null | undefined,
  userId: string | null | undefined,
) {
  return `${STORAGE_PREFIX}${slug}.${scope(advertiserId, userId)}.token`;
}

function cacheKey(
  slug: string,
  advertiserId: string | null | undefined,
  userId: string | null | undefined,
) {
  return `${STORAGE_PREFIX}${slug}.${scope(advertiserId, userId)}.cache`;
}

// Synchronous write-through to localStorage so a crash between keystroke and
// the next debounced server save still survives the refresh.
function writeLocalCache(
  slug: string,
  advertiserId: string | null | undefined,
  userId: string | null | undefined,
  payload: SaveInput,
): void {
  try {
    localStorage.setItem(
      cacheKey(slug, advertiserId, userId),
      JSON.stringify({ ...payload, savedAt: Date.now() }),
    );
  } catch { /* private mode / quota — non-fatal */ }
}

function readLocalCache(
  slug: string,
  advertiserId: string | null | undefined,
  userId: string | null | undefined,
): (SaveInput & { savedAt: number }) | null {
  try {
    const raw = localStorage.getItem(cacheKey(slug, advertiserId, userId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function clearLocalCache(
  slug: string,
  advertiserId: string | null | undefined,
  userId: string | null | undefined,
): void {
  try { localStorage.removeItem(cacheKey(slug, advertiserId, userId)); } catch { /* noop */ }
}

// Sign-in promotion: when the wizard transitions from anonymous to a chosen
// (advertiser, user) pair, copy the pre-existing anon draft into the
// destination slot so the in-progress wizard state survives sign-in. No-op
// when either dimension is still missing or the destination slot already has
// its own draft.
function promoteAnon(slug: string, advertiserId: string, userId: string): boolean {
  if (!advertiserId || !userId) return false;
  try {
    const destToken = localStorage.getItem(tokenKey(slug, advertiserId, userId));
    if (destToken) return false; // destination already has its own draft

    const anonToken = localStorage.getItem(tokenKey(slug, null, null));
    if (!anonToken) return false;

    localStorage.setItem(tokenKey(slug, advertiserId, userId), anonToken);
    const anonCache = localStorage.getItem(cacheKey(slug, null, null));
    if (anonCache) {
      localStorage.setItem(cacheKey(slug, advertiserId, userId), anonCache);
    }
    localStorage.removeItem(tokenKey(slug, null, null));
    localStorage.removeItem(cacheKey(slug, null, null));
    return true;
  } catch {
    return false;
  }
}

/**
 * Replace the locally-stored draft token for a (publisher, advertiser, user)
 * triple with one provided externally (e.g. via a `?resume=...` magic link
 * from a recovery email). The hook will pick it up on its next mount and load
 * the matching draft. Pass `advertiserId = null` and/or `userId = null` for
 * an anonymous resume.
 */
export function adoptDraftToken(
  slug: string,
  token: string,
  advertiserId: string | null,
  userId: string | null,
): void {
  if (!token || token.length < 16) return;
  try { localStorage.setItem(tokenKey(slug, advertiserId, userId), token); } catch { /* private mode */ }
  // CTV-468: ?resume=<token> is the magic-link draft-resume path.
  analytics.track(EVENTS.DRAFT_RESUMED, { station_slug: slug });
}

function emptyWaymarkState(): WaymarkDraftState {
  return {
    status: 'idle',
    sourceUrl: null,
    topicId: null,
    generationRequestId: null,
    videoId: null,
    renderedUrl: null,
    errorCode: null,
    errorMessage: null,
  };
}

function generateToken(): string {
  // 128 bits of randomness, hex-encoded. Opaque and unguessable.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Server-side draft persistence for the self-service wizard.
 *
 * - Scopes the draft to (station, advertiser-or-anon, user-or-anon) so each
 *   (user, seat) pair keeps its own in-progress draft. See ADR 0016.
 * - Reads or creates a draft token in localStorage (per scope)
 * - Loads any existing draft on mount and returns it for hydration
 * - Exposes a debounced `save` to call on every form/step change
 * - Exposes `clear` to wipe the token after submission
 *
 * Pass `advertiserId = null` and/or `userId = null` for unsigned users;
 * promote to real ids when sign-in (and seat resolution) lands. The hook
 * handles the anon→authed migration of the existing draft so wizard state
 * isn't lost in the handoff.
 */
export function useSelfServiceDraft(
  slug: string,
  advertiserId: string | null,
  userId: string | null,
) {
  const [loaded, setLoaded] = React.useState<LoadedDraft | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  // Tracked in state (not just a ref) so consumers re-render when a token is
  // adopted from localStorage or freshly generated on first save. Needed by
  // WaymarkProvider, which can't kick off until it has a token.
  const [token, setToken] = React.useState<string | null>(() => {
    try { return localStorage.getItem(tokenKey(slug, advertiserId, userId)); } catch { return null; }
  });
  const tokenRef = React.useRef<string | null>(token);
  const saveTimer = React.useRef<number | null>(null);
  const pendingPayload = React.useRef<SaveInput | null>(null);
  const advertiserRef = React.useRef<string | null>(advertiserId);
  const userIdRef = React.useRef<string | null>(userId);

  // CTV-554: synchronously reset `loaded` and `isLoading` in render when
  // the (advertiserId, userId) identity changes. The load effect below
  // does the same in useEffect, but effects run AFTER commit — so on the
  // first render under the new identity, the loader would see stale
  // `loaded` from the previous slot and mount the wizard body with
  // cross-contaminated seed values (the body's useForm picks up the
  // stale formData, then its initial-save effect flushes the wrong
  // values into the new slot). React's "set state during render" path
  // discards the in-flight render output and re-renders synchronously
  // with the reset state, so the loader's spinner gate fires on the
  // same render the identity changed. The "Advertise with another
  // business" flow exposed this; without the reset, both the old and
  // the new advertiser's draft rows ended up cross-contaminated.
  //
  // Deliberately NOT touching advertiserRef/userIdRef/tokenRef here —
  // those stay on the OLD identity until the ref-update effects fire,
  // which is what makes the load-effect cleanup's flush-against-old-
  // identity work. The loader's spinner gate keeps the body unmounted
  // during this window, so no consumer reads the lagging refs.
  const lastIdentityRef = React.useRef<{ adv: string | null; user: string | null }>({
    adv: advertiserId, user: userId,
  });
  if (
    lastIdentityRef.current.adv !== advertiserId ||
    lastIdentityRef.current.user !== userId
  ) {
    lastIdentityRef.current = { adv: advertiserId, user: userId };
    setLoaded(null);
    setIsLoading(true);
  }

  React.useEffect(() => {
    advertiserRef.current = advertiserId;
  }, [advertiserId]);

  React.useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // Load (or re-load on identity change). When the (advertiserId, userId)
  // pair flips from (null, null) to real ids we first migrate the anon slot
  // into the destination slot so an in-progress wizard survives sign-in.
  // Otherwise we just re-key.
  //
  // Reset `isLoading` and re-read the token synchronously on identity flip
  // so the loader's spinner gate fires for the new slot (otherwise the body
  // would stay mounted with stale draft data for a render).
  React.useEffect(() => {
    setIsLoading(true);
    if (advertiserId && userId) {
      promoteAnon(slug, advertiserId, userId);
    }
    const freshToken = (() => {
      try { return localStorage.getItem(tokenKey(slug, advertiserId, userId)); }
      catch { return null; }
    })();
    tokenRef.current = freshToken;
    setToken(freshToken);

    let cancelled = false;
    (async () => {
      if (!isSelfServiceConfigured) {
        setIsLoading(false);
        return;
      }
      if (!freshToken) {
        // No token for this scope — check local cache for a crash-rescue.
        const cached = readLocalCache(slug, advertiserId, userId);
        if (!cancelled) {
          if (cached) {
            setLoaded({
              currentStep: cached.currentStep,
              formData: cached.formData,
              creatives: cached.creatives ?? null,
              lastSavedAt: new Date(cached.savedAt).toISOString(),
              waymark: emptyWaymarkState(),
            });
          } else {
            // Reset wizard state when switching to a slot with no draft so
            // the previous slot's data doesn't bleed into the new one.
            setLoaded(null);
          }
          setIsLoading(false);
        }
        return;
      }
      try {
        interface LoadedDraftRow {
          current_step: number;
          form_data: Record<string, unknown> | null;
          creatives: CreativeSlotState[] | null;
          last_saved_at: string;
          waymark_status?: WaymarkStatus | null;
          waymark_source_url?: string | null;
          waymark_topic_id?: string | null;
          waymark_generation_request_id?: string | null;
          waymark_video_id?: string | null;
          waymark_rendered_url?: string | null;
          waymark_error_code?: string | null;
          waymark_error_message?: string | null;
        }
        const { draft } = await apiClient.post<{ draft: LoadedDraftRow | null }>('/drafts/load', {
          draftToken: freshToken,
          slug,
          ...(advertiserId ? { advertiserId } : {}),
        });
        if (!cancelled && draft) {
          setLoaded({
            currentStep: draft.current_step,
            formData: draft.form_data ?? {},
            creatives: Array.isArray(draft.creatives) ? draft.creatives : null,
            lastSavedAt: draft.last_saved_at,
            waymark: {
              status: draft.waymark_status ?? 'idle',
              sourceUrl: draft.waymark_source_url ?? null,
              topicId: draft.waymark_topic_id ?? null,
              generationRequestId: draft.waymark_generation_request_id ?? null,
              videoId: draft.waymark_video_id ?? null,
              renderedUrl: draft.waymark_rendered_url ?? null,
              errorCode: draft.waymark_error_code ?? null,
              errorMessage: draft.waymark_error_message ?? null,
            },
          });
        } else if (!cancelled) {
          // Server reachable but says null for this token. We HAD a token
          // (freshToken non-null — we wouldn't be in this branch
          // otherwise), so the server-side row is gone (admin cleanup,
          // expired draft, etc.). The local token is orphaned and the
          // local cache is a stale snapshot of the deleted state — both
          // need to go, or the next save will mint a new server row
          // with the stale cached formData (the bug behind the
          // post-cleanup re-contamination, where deleted-on-server
          // drafts came back with the old polluted businessName).
          // Downstream effect: WaymarkProvider also stops trying
          // /waymark/{token,state} with the orphaned token (those return
          // 404 hard rather than null, so they otherwise spam the console).
          try {
            localStorage.removeItem(tokenKey(slug, advertiserId, userId));
            clearLocalCache(slug, advertiserId, userId);
          } catch { /* private mode — non-fatal */ }
          tokenRef.current = null;
          setToken(null);
          setLoaded(null);
        }
      } catch (e) {
        // Stale-token rescue: if the draft we have a token for belongs to
        // another user, a different station, or a different advertiser, drop
        // the local token so the next save mints a fresh one. Without this
        // the hook would load the old token forever and every save would 403.
        if (
          e instanceof ApiError &&
          e.status === 403 &&
          typeof e.message === 'string' &&
          /owned by another user|bound to a different/i.test(e.message)
        ) {
          try {
            localStorage.removeItem(tokenKey(slug, advertiserId, userId));
            clearLocalCache(slug, advertiserId, userId);
          } catch { /* private mode — non-fatal */ }
          tokenRef.current = null;
          if (!cancelled) {
            setToken(null);
            setLoaded(null);
            setIsLoading(false);
          }
          return;
        }
        // Server unreachable — fall back to local cache.
        console.warn('[selfservice-draft] load failed, falling back to local cache', e);
        const cached = readLocalCache(slug, advertiserId, userId);
        if (!cancelled && cached) {
          setLoaded({
            currentStep: cached.currentStep,
            formData: cached.formData,
            creatives: cached.creatives ?? null,
            lastSavedAt: new Date(cached.savedAt).toISOString(),
            waymark: emptyWaymarkState(),
          });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      // If the buyer typed within the debounce window before switching
      // identity, flush against the OLD identity (advertiserRef/userIdRef
      // here still hold the previous values — the ref-update effects above
      // run AFTER this cleanup). Without this the pending payload would
      // land in the NEW slot's draft row when the timer fires.
      if (pendingPayload.current && saveTimer.current !== null) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
        void flushRef.current?.();
      }
    };
  }, [slug, advertiserId, userId]);

  // Adopt the server-returned draft token when it differs from the one we
  // POSTed. The server collapses concurrent/stale tokens onto the canonical
  // seat-row token (see drafts.service.ts ON CONFLICT path), so we mirror that
  // back into localStorage/tokenRef. Without this, the next debounced save
  // would re-POST with the now-orphaned local token and re-trigger the same
  // collapse on every save.
  const adoptServerToken = React.useCallback(
    (
      advertiser: string | null,
      user: string | null,
      serverToken: string | undefined | null,
    ) => {
      if (!serverToken || serverToken === tokenRef.current) return;
      tokenRef.current = serverToken;
      try { localStorage.setItem(tokenKey(slug, advertiser, user), serverToken); } catch { /* noop */ }
      setToken(serverToken);
    },
    [slug],
  );

  const flush = React.useCallback(async () => {
    const payload = pendingPayload.current;
    if (!payload || !isSelfServiceConfigured) return;
    pendingPayload.current = null;
    const advertiser = advertiserRef.current;
    const user = userIdRef.current;

    if (!tokenRef.current) {
      tokenRef.current = generateToken();
      try { localStorage.setItem(tokenKey(slug, advertiser, user), tokenRef.current); } catch { /* noop */ }
      setToken(tokenRef.current);
      analytics.track(EVENTS.DRAFT_CREATED, { station_slug: slug });
    }

    try {
      const res = await apiClient.post<{ ok?: boolean; draftId?: string; draftToken?: string; lastSavedAt?: string }>('/drafts', {
        draftToken: tokenRef.current,
        slug,
        currentStep: payload.currentStep,
        formData: payload.formData,
        creatives: payload.creatives ?? null,
        ...(advertiser ? { advertiserId: advertiser } : {}),
      });
      adoptServerToken(advertiser, user, res?.draftToken);
    } catch (e) {
      // If the draft is owned by a different user or bound to a different
      // advertiser (we previously saved it under another session), the server
      // returns 403 with a specific message. Drop the stale token + cache and
      // mint a new one so the NEXT save creates a fresh row owned by the
      // current user/advertiser.
      if (
        e instanceof ApiError &&
        e.status === 403 &&
        typeof e.message === 'string' &&
        /owned by another user|bound to a different/i.test(e.message)
      ) {
        try {
          localStorage.removeItem(tokenKey(slug, advertiser, user));
          clearLocalCache(slug, advertiser, user);
        } catch { /* private mode — non-fatal */ }
        tokenRef.current = null;
        // Restore the pending payload AND retry immediately with a fresh
        // token (minted inside the next flush).
        pendingPayload.current = payload;
        try {
          const res = await apiClient.post<{ ok?: boolean; draftId?: string; draftToken?: string; lastSavedAt?: string }>('/drafts', {
            draftToken: (tokenRef.current = generateToken()),
            slug,
            currentStep: payload.currentStep,
            formData: payload.formData,
            creatives: payload.creatives ?? null,
            ...(advertiser ? { advertiserId: advertiser } : {}),
          });
          try { localStorage.setItem(tokenKey(slug, advertiser, user), tokenRef.current); } catch { /* noop */ }
          adoptServerToken(advertiser, user, res?.draftToken);
          pendingPayload.current = null;
        } catch (retryErr) {
          console.warn('[selfservice-draft] save retry after 403-rescue failed', retryErr);
        }
        return;
      }
      console.warn('[selfservice-draft] save failed', e);
    }
  }, [slug, adoptServerToken]);

  // The load-effect cleanup needs to call the latest `flush` without
  // re-running on every `flush` identity change (which would re-trigger the
  // full load on every save). Holding it in a ref keeps the cleanup current.
  const flushRef = React.useRef(flush);
  React.useEffect(() => { flushRef.current = flush; }, [flush]);

  const save = React.useCallback(
    (input: SaveInput) => {
      pendingPayload.current = input;
      // Synchronous local cache write — survives any crash in the debounce
      // window before the server save fires.
      writeLocalCache(slug, advertiserRef.current, userIdRef.current, input);
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        saveTimer.current = null;
        void flush();
      }, SAVE_DEBOUNCE_MS);
    },
    [flush, slug]
  );

  // Flush on tab hide / pagehide — mobile Safari kills backgrounded tabs.
  React.useEffect(() => {
    const onHide = () => { void flush(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
    };
  }, [flush]);

  const clear = React.useCallback(() => {
    const advertiser = advertiserRef.current;
    const user = userIdRef.current;
    try {
      localStorage.removeItem(tokenKey(slug, advertiser, user));
    } catch { /* noop */ }
    clearLocalCache(slug, advertiser, user);
    tokenRef.current = null;
    setToken(null);
    pendingPayload.current = null;
    if (saveTimer.current !== null) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
  }, [slug]);

  const getToken = React.useCallback((): string | null => tokenRef.current, []);

  return { loaded, isLoading, save, clear, getToken, token };
}
